/**
 * PDP "Add to subscription" CTA.
 *
 * For a customer who arrived from Klaviyo with a stored quick-add credential, this replaces the
 * picker's Add to Cart CTA and posts the currently selected offer to the quick-add endpoint, which
 * appends it to the customer's existing Recharge subscription. Nothing touches the Shopify cart.
 *
 * The browser sends IDENTIFIERS ONLY — variant id, selling plan id, quantity (plus an optional
 * discount code passed through verbatim). Cadence, interval and price are resolved server-side.
 *
 * On a confirmed add it also fires a Klaviyo event describing the selection — dog weight, quantity,
 * when it will be delivered, and the price before and after the subscription discount — so flows can
 * follow up on it. That is best-effort and never gates the redirect.
 */
const QUICK_ADD_STORAGE_KEY = 'wuffes_recharge_quick_add';

/**
 * Where to send the customer once the item is on their subscription: the same
 * place the header account icon goes.
 *
 * Those links are rendered with the portal login URL and then rewritten by
 * global.js on DOMContentLoaded to the deep link cached in
 * `localStorage.customer_portal_url`, so a customer who has been in the portal
 * before lands straight on it instead of logging in again. Reading the anchor
 * rather than the storage key keeps this in step with whatever global.js
 * decides, and matches how section-product-main.js resolves the same link.
 */
const RECHARGE_PORTAL_LINK_SELECTOR = '.js__recharge_login_link[href]';

/**
 * Used only when the page has no account link to read (e.g. a headerless layout).
 * Kept identical to the header account icon's own href so both paths land in the
 * same place; update it here if that link ever changes.
 */
const RECHARGE_PORTAL_FALLBACK_URL = 'https://wuffes.com/tools/recurring/login?redirect=customer_portal%2Foverview';

const QUICK_ADD_REDIRECT_DELAY_MS = 1200;
const QUICK_ADD_CTA_HIDDEN_CLASS = 'pdp-add-to-subscription__cta--hidden';
const QUICK_ADD_BUTTON_VISIBLE_CLASS = 'pdp-add-to-subscription__button--visible';
const QUICK_ADD_NOTICE_CLASS = 'pdp-add-to-subscription__error--notice';

/** Set pre-paint in layout/theme.liquid; hides the default CTA before it can flash. */
const QUICK_ADD_ROOT_CLASS = 'wuffes-quick-add';

/**
 * Every CTA this button stands in for: the picker's Add to Cart plus the
 * one-time-purchase buttons from _pdp-picker-otp-buttons.liquid.
 */
const QUICK_ADD_REPLACED_CTA_SELECTOR = '[data-submit-button], [data-otp-checkout-button]';

/** Set by _pdp-picker-purchase-toggle.liquid; checked means "subscription". */
const QUICK_ADD_PURCHASE_TOGGLE_SELECTOR = '[data-purchase-type-toggle]';

/**
 * The quantity steppers from _pdp-picker-qty-selector.liquid. They mutate the
 * quantity input's value from a click handler bound on the button itself and
 * dispatch no event, so the price display has to key off the click — see
 * onPickerChange for why listening on the form is what makes that safe.
 */
const QUICK_ADD_QTY_STEPPER_SELECTOR = '[data-picker-qty-decrease], [data-picker-qty-increase]';

const QUICK_ADD_MESSAGE_NO_OFFER = 'Please select an option first.';
const QUICK_ADD_MESSAGE_SUBSCRIPTION_REQUIRED =
  'Please choose a subscription plan — one-time purchases cannot be added to a subscription.';
const QUICK_ADD_MESSAGE_FALLBACK_ERROR = 'Something went wrong. Please try again.';
const QUICK_ADD_MESSAGE_UNCONFIRMED =
  "We couldn't confirm the result. Your subscription may already have been updated — check your account before trying again.";

/** Markers the endpoint may return when the account has no active subscription. */
const QUICK_ADD_NO_SUBSCRIPTION_PATTERN = /no[\s_-]*(active[\s_-]*)?subscription/i;

/** Stated as the delivery when no explicit first-charge date is configured. */
const QUICK_ADD_DELIVERY_NEXT_ORDER = 'with the next upcoming order';

/** Klaviyo event fired after a confirmed add; the block may override the name. */
const QUICK_ADD_KLAVIYO_EVENT = 'Added To Subscription';

class PdpAddToSubscription extends HTMLElement {
  constructor() {
    super();
    this.form = null;
    this.button = null;
    this.labelEl = null;
    this.errorEl = null;
    this.defaultLabel = '';
    this.hiddenCtas = [];
    this.isSubmitting = false;
    this.redirectTimer = null;
    this.priceEl = null;
    this.discountedPriceEl = null;
    this.oldPriceEl = null;
    this.onClick = this.onClick.bind(this);
    this.onPickerChange = this.onPickerChange.bind(this);
  }

  connectedCallback() {
    this.form = this.closest('[data-picker-form]');
    this.button = this.querySelector('[data-add-to-subscription]');
    this.errorEl = this.querySelector('[data-error]');
    if (!this.form || !this.button) return;

    // Block added but never configured — never take the CTA over with a dead endpoint.
    if (!this.dataset.endpoint) return;

    this.labelEl = this.button.querySelector('[data-button-label]') || this.button;
    this.defaultLabel = this.labelEl.textContent;

    const credential = this.readStoredCredential();
    // No credential (or storage unavailable): leave the standard Add to Cart flow untouched.
    if (!credential) return;

    this.hideExistingCta();
    this.button.classList.add(QUICK_ADD_BUTTON_VISIBLE_CLASS);
    this.button.addEventListener('click', this.onClick);

    this.priceEl = this.querySelector('[data-prices]');
    if (!this.priceEl) return;

    this.discountedPriceEl = this.querySelector('[data-sub-price-discounted]');
    this.oldPriceEl = this.querySelector('[data-sub-price-old]');

    this.form.addEventListener('change', this.onPickerChange);
    this.form.addEventListener('click', this.onPickerChange);
    this.updatePrices();
  }

  disconnectedCallback() {
    if (this.button) {
      this.button.removeEventListener('click', this.onClick);
      this.button.classList.remove(QUICK_ADD_BUTTON_VISIBLE_CLASS);
    }

    if (this.form) {
      this.form.removeEventListener('change', this.onPickerChange);
      this.form.removeEventListener('click', this.onPickerChange);
    }

    // Theme-editor section reloads must not leave an orphaned hidden CTA behind,
    // nor a notice stranded in the CTA block after standDownToCart moved it.
    this.restoreExistingCta();
    if (this.errorEl && this.errorEl.parentElement !== this) {
      this.append(this.errorEl);
    }

    if (this.redirectTimer) {
      clearTimeout(this.redirectTimer);
      this.redirectTimer = null;
    }

    this.isSubmitting = false;
  }

  /* ---------- credential ---------- */

  readStoredCredential() {
    let raw = null;

    try {
      raw = localStorage.getItem(QUICK_ADD_STORAGE_KEY);
    } catch (error) {
      return null;
    }

    if (!raw) return null;

    let stored = null;
    try {
      stored = JSON.parse(raw);
    } catch (error) {
      return null;
    }

    if (!stored || !stored.email || !stored.token || !stored.savedAt) return null;

    // Expiry is not re-checked here. recharge-quick-add-capture.js owns the TTL
    // and prunes on every page load, and it is loaded ahead of this script in
    // both layouts, so anything still in storage by now is within the window.
    // Duplicating the threshold risked the worse-than-cosmetic case of this
    // component being stricter than the pre-paint bootstrap: the root class
    // would hide Add to Cart while the subscription button stayed hidden too,
    // leaving the picker with no CTA at all.
    return { email: stored.email, token: stored.token };
  }

  /* ---------- selection ---------- */

  /**
   * Mirrors ProductPicker.getActiveOffer() in section-product-main.js: the visible dogsize group
   * is the one whose computed display is `block`, and the active offer is the checked radio inside
   * it. Scoped to this picker form rather than the document, and resolved at click time so the
   * live selection is always used.
   */
  getActiveOffer() {
    const groups = this.form.querySelectorAll('.picker-dogsize_offers-group');

    for (const group of groups) {
      if (window.getComputedStyle(group).display === 'block') {
        return group.querySelector('.picker-offer_input:checked');
      }
    }

    return null;
  }

  /**
   * Whether the shopper is buying one-time rather than subscribing.
   *
   * The purchase-type toggle is the only evidence, exactly as in
   * ProductPicker.updateOfferGroups: checked means subscription. When the
   * product has no subscription option that toggle renders disabled and
   * unchecked, which is correctly read as one-time.
   *
   * No toggle means the picker offers no way to choose one-time, so this is a
   * subscription. Two things that look like evidence are not:
   *
   * - `data-selling-plan` on the selected radio. The OTP group is a clone of
   *   the subscription offers with only the input id/name and label `for`
   *   rewritten, so a cloned OTP radio still carries the real numeric plan id.
   * - Which group the selection sits in. ProductPicker composes the visible
   *   group id as `<group>-<purchaseType>`, and with no toggle on the page
   *   `purchaseType` falls through to 'otp' — so on a subscription-only picker
   *   *every* selection lives in a group named `--otp`. Treating that as a
   *   one-time purchase rejected every valid add on the Dental PDP.
   */
  isOneTimePurchase() {
    const toggle = this.form.querySelector(QUICK_ADD_PURCHASE_TOGGLE_SELECTOR);

    return toggle ? !toggle.checked : false;
  }

  /** Mirrors ProductPicker.qtyMultiplier, read from this form's quantity input. */
  getQuantityMultiplier() {
    const qtyInput = this.form.querySelector('[data-picker-qty-input]');
    if (!qtyInput) return 1;
    return parseInt(qtyInput.value, 10) || 1;
  }

  /* ---------- CTA visibility ---------- */

  /**
   * Hides every purchase path this button replaces: Add to Cart and the
   * one-time-purchase buttons. A quick-add customer is adding to an existing
   * subscription, so a one-time buy alongside it is a route the flow cannot
   * honour. Hiding is class-based rather than a style write, so ProductPicker
   * keeps ownership of the OTP buttons' own inline display.
   */
  hideExistingCta() {
    this.hiddenCtas = Array.from(this.form.querySelectorAll(QUICK_ADD_REPLACED_CTA_SELECTOR));
    this.hiddenCtas.forEach((cta) => cta.classList.add(QUICK_ADD_CTA_HIDDEN_CLASS));

    // The bootstrap can only set this from what was in storage pre-paint; a credential that
    // arrived in the URL is first known here, so the takeover has to set it too.
    document.documentElement.classList.add(QUICK_ADD_ROOT_CLASS);
  }

  restoreExistingCta() {
    this.hiddenCtas.forEach((cta) => cta.classList.remove(QUICK_ADD_CTA_HIDDEN_CLASS));
    this.hiddenCtas = [];

    document.documentElement.classList.remove(QUICK_ADD_ROOT_CLASS);
  }

  /* ---------- UI state ---------- */

  setLabel(text) {
    if (this.labelEl && text) this.labelEl.textContent = text;
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message || QUICK_ADD_MESSAGE_FALLBACK_ERROR;
  }

  clearError() {
    if (this.errorEl) this.errorEl.textContent = '';
  }

  startSubmitting() {
    this.isSubmitting = true;
    this.clearError();
    this.button.disabled = true;
    this.button.setAttribute('aria-busy', 'true');
    this.setLabel(this.dataset.processingText);
    // "Adding… | $41.97" reads as a price for the progress state rather than the item.
    if (this.priceEl) this.priceEl.hidden = true;
  }

  stopSubmitting() {
    this.isSubmitting = false;
    this.button.disabled = false;
    this.button.removeAttribute('aria-busy');
    this.setLabel(this.defaultLabel);
    this.updatePrices();
  }

  /* ---------- price display ---------- */

  /**
   * Recomputed from the picker on every interaction that can move the price:
   * dog size, offer, purchase toggle and a typed quantity all fire a bubbling
   * `change`, and the quantity steppers are caught by their click.
   *
   * Bound on the form rather than on the controls themselves, which is what
   * makes reading the picker's state here safe without knowing script order:
   * ProductPicker binds its own handlers on the targets, and a listener on an
   * ancestor runs in the bubbling phase — after those have already switched the
   * visible offer group and written the new quantity.
   */
  onPickerChange(event) {
    if (event.type === 'click' && !event.target.closest(QUICK_ADD_QTY_STEPPER_SELECTOR)) return;

    this.updatePrices();
  }

  /**
   * The extra discount as a multiplier (0.7 for 30% off), or 1 when it is not
   * being folded into the displayed price.
   *
   * Cosmetic only. The real reduction is the Recharge discount resolved
   * server-side from the code, so this percentage is the merchant's promise
   * that the two match — the same caveat that governs extraDiscountLabel().
   */
  extraDiscountFactor() {
    if (this.dataset.extraDiscountInPrice === undefined) return 1;

    const percent = parseFloat(this.dataset.extraDiscountPercent);
    if (!Number.isFinite(percent) || percent <= 0 || percent >= 100) return 1;

    return 1 - percent / 100;
  }

  /** "$59.95" or "$1,234.56" -> 59.95 / 1234.56. Null when there is no number to read. */
  parseMoney(value) {
    if (!value) return null;

    const match = String(value).match(/[\d,.]+/);
    if (!match) return null;

    const amount = parseFloat(match[0].replace(/,/g, ''));
    return Number.isFinite(amount) ? amount : null;
  }

  /** Rewrites the amount inside a money string, keeping its currency formatting. */
  formatMoneyLike(template, amount) {
    return template.replace(/[\d,.]+/, amount.toFixed(2));
  }

  /**
   * Subscription price on the button, full price struck through beside it.
   *
   * Both figures come off the selected offer radio rather than being recomputed:
   * `data-discounted-price` already carries that offer's subscription discount,
   * so the chain is exactly full → subscription discount → extra discount. The
   * quantity selector multiplies on top, mirroring ProductPicker.multiplyPrice.
   */
  updatePrices() {
    if (!this.priceEl || !this.discountedPriceEl || !this.oldPriceEl) return;

    const activeOffer = this.getActiveOffer();
    if (!activeOffer) return;

    const fullTemplate = activeOffer.getAttribute('data-default-price');
    const discountedTemplate = activeOffer.getAttribute('data-discounted-price');
    const full = this.parseMoney(fullTemplate);
    const discounted = this.parseMoney(discountedTemplate);
    if (full === null || discounted === null) return;

    const multiplier = this.getQuantityMultiplier();
    const fullText = this.formatMoneyLike(fullTemplate, full * multiplier);
    const discountedText = this.formatMoneyLike(
      discountedTemplate,
      discounted * multiplier * this.extraDiscountFactor()
    );

    this.discountedPriceEl.textContent = discountedText;
    this.oldPriceEl.textContent = fullText;
    this.oldPriceEl.hidden = fullText === discountedText;

    // Rendered hidden: _pdp-picker-offers.liquid only checks the default offer on a
    // 50ms timer, so until it does there is no price and the separator would sit alone.
    this.priceEl.hidden = false;
  }

  /* ---------- idempotency ---------- */

  /**
   * A key the server uses to recognise a repeat of *this* add.
   *
   * Deliberately stable across retries of the same selection: after an error the
   * customer clicks again, and the first attempt may have succeeded without the
   * browser being able to tell. Replaying the key makes that a no-op instead of
   * a second line on their charge. Changing dog size, frequency or quantity is a
   * different intent, so it earns a fresh key.
   */
  idempotencyKeyFor(signature) {
    if (this.pendingSignature !== signature || !this.pendingKey) {
      this.pendingSignature = signature;
      this.pendingKey = this.randomKey();
    }

    return this.pendingKey;
  }

  randomKey() {
    if (crypto.randomUUID) return crypto.randomUUID();

    // Older Safari: uuid is not required, only that keys do not collide.
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  }

  /* ---------- submit ---------- */

  onClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.isSubmitting) return;

    this.startSubmitting();

    const endpoint = this.dataset.endpoint;
    const credential = endpoint ? this.readStoredCredential() : null;
    if (!endpoint || !credential) {
      this.failWith(this.dataset.errorText);
      return;
    }

    const activeOffer = this.getActiveOffer();
    if (!activeOffer) {
      this.failWith(QUICK_ADD_MESSAGE_NO_OFFER);
      return;
    }

    if (this.isOneTimePurchase()) {
      this.failWith(QUICK_ADD_MESSAGE_SUBSCRIPTION_REQUIRED);
      return;
    }

    const variantId = activeOffer.getAttribute('data-variant-id');
    if (!variantId) {
      this.failWith(QUICK_ADD_MESSAGE_NO_OFFER);
      return;
    }

    // Cadence is never computed here — `data-selling-plan` already holds the selling plan ID.
    // Blank or 'otp' cannot be added to a subscription. This is a backstop only: the OTP
    // radios are clones that keep a real plan id, so isOneTimePurchase is the real guard.
    const sellingPlanId = activeOffer.getAttribute('data-selling-plan');
    if (!sellingPlanId || sellingPlanId === 'otp') {
      this.failWith(QUICK_ADD_MESSAGE_SUBSCRIPTION_REQUIRED);
      return;
    }

    const baseQuantity = parseInt(activeOffer.getAttribute('data-quantity'), 10) || 1;
    const qtyMultiplier = this.getQuantityMultiplier();
    const quantity = baseQuantity * qtyMultiplier;

    const payload = {
      email: credential.email,
      token: credential.token,
      variant_id: variantId,
      selling_plan_id: sellingPlanId,
      quantity: quantity,
      idempotency_key: this.idempotencyKeyFor(`${variantId}:${sellingPlanId}:${quantity}`),
    };

    // Passed through verbatim, and only when the block actually rendered the attribute.
    const extraDiscountCode = this.dataset.extraDiscountCode;
    if (extraDiscountCode) payload.extra_discount_code = extraDiscountCode;

    // Optional first charge date. Sent verbatim and only when set — omitting it
    // is what makes the server keep the customer's own next charge date. The
    // server validates the value; the browser never parses or defaults it.
    const nextChargeDate = this.dataset.nextChargeDate;
    if (nextChargeDate) payload.next_charge_date = nextChargeDate;

    // Read from the same radio the payload came from, and resolved now rather
    // than on success: the customer is free to change the picker while the
    // request is in flight, and the event must describe what was actually added.
    const trackingEvent = {
      email: credential.email,
      properties: this.buildTrackingProperties(activeOffer, quantity, qtyMultiplier),
    };

    this.submit(endpoint, payload, trackingEvent);
  }

  submit(endpoint, payload, trackingEvent) {
    fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then((response) =>
        response
          .json()
          .catch(() => null)
          .then((data) => ({ ok: response.ok, data: data }))
      )
      .then((result) => {
        // `ok: true` with `discount_applied: false` is still a success — the subscription exists.
        // `duplicate: true` is the server recognising a replayed idempotency key: same outcome.
        if (result.ok && result.data && result.data.ok === true) {
          this.onSuccess(trackingEvent);
          return;
        }

        if (this.isNoSubscriptionResponse(result.data)) {
          this.standDownToCart(this.dataset.noSubscriptionText);
          return;
        }

        // A 2xx whose body could not be read proves nothing either way, so never
        // claim success — but say so honestly rather than blaming the customer.
        // The idempotency key makes the retry they are about to attempt safe.
        this.failWith(result.ok ? QUICK_ADD_MESSAGE_UNCONFIRMED : this.dataset.errorText);
      })
      .catch(() => {
        // Network failure — never reveal detail, never redirect.
        this.failWith(this.dataset.errorText);
      });
  }

  /** Best-effort detection of the "no active subscription" case from the error body. */
  isNoSubscriptionResponse(data) {
    if (!data) return false;

    return [data.error, data.code, data.message, data.reason].some(
      (value) => typeof value === 'string' && QUICK_ADD_NO_SUBSCRIPTION_PATTERN.test(value)
    );
  }

  failWith(message) {
    this.showError(message);
    this.stopSubmitting();
  }

  /**
   * The account has no subscription to add to, so this CTA can never succeed for
   * this customer: state that, retire the button, and hand the picker back to
   * the standard Add to Cart flow so the visit is still shoppable.
   */
  standDownToCart(message) {
    this.stopSubmitting();
    this.showError(message);
    this.errorEl?.classList.add(QUICK_ADD_NOTICE_CLASS);

    this.button.removeEventListener('click', this.onClick);
    this.button.classList.remove(QUICK_ADD_BUTTON_VISIBLE_CLASS);

    this.restoreExistingCta();

    // This block sits below the CTA in the picker, so the notice has to move to
    // read as an explanation of the Add to Cart button it now sits above.
    const cta = this.form.querySelector('[data-submit-button]');
    if (cta && this.errorEl) cta.before(this.errorEl);

    // The credential is valid but useless for this account — without clearing it
    // every later PDP would paint a button that is guaranteed to fail again.
    try {
      localStorage.removeItem(QUICK_ADD_STORAGE_KEY);
    } catch (error) {
      // Storage unavailable; the button is already retired for this page view.
    }
  }

  /* ---------- analytics ---------- */

  /**
   * The extra one-time discount as flow copy can print it ('30%'), or null when
   * the block is not running one.
   *
   * Presence follows the code, which the block renders only when the merchant
   * has both ticked the toggle and entered a code — the same condition that
   * decides whether the request asks for a discount at all. The percentage is a
   * block setting rather than anything the browser can compute: the real figure
   * lives on the Recharge discount and is applied server-side.
   *
   * Note this reports what was *requested*. The server may still decline the
   * code (not allowlisted, over ceiling, wrong product), and when the charge is
   * not yet ready the discount lands minutes later via the scheduled retry.
   */
  extraDiscountLabel() {
    const code = this.dataset.extraDiscountCode;
    const percent = this.dataset.extraDiscountPercent;
    if (!code || !percent) return null;

    return `${percent}%`;
  }

  /**
   * What the customer chose, in the same terms the picker uses everywhere else.
   *
   * `data-dogsize` is the weight band label ('65-100lbs'), matching what
   * buildKlaviyoTags() reads in section-product-main.js.
   *
   * **Delivery.** `delivery_date` is always present so flow copy never has to
   * branch: the explicit first-charge date when the block sets one, otherwise a
   * plain statement that the item rides along with the customer's next order —
   * which is exactly what the server does when no date is sent.
   *
   * **Prices.** Read from the selected radio rather than recomputed: the offer
   * block already applies the tier discount configured per offer (10% / 12.5% /
   * 15% for 1 / 2 / 3 tubs on this product), so these are the very figures the
   * customer saw. `data-*-price` covers the offer's own tub count; the quantity
   * selector multiplies on top, mirroring ProductPicker.multiplyPrice. These are
   * the subscription prices; any extra one-time discount is reported separately
   * as `extra_one_time_discount` and is applied by Recharge on top.
   */
  buildTrackingProperties(activeOffer, quantity, qtyMultiplier) {
    const multiplier = qtyMultiplier || 1;
    const original = this.parseMoney(activeOffer.getAttribute('data-default-price'));
    const discounted = this.parseMoney(activeOffer.getAttribute('data-discounted-price'));
    const toMoney = (amount) => (amount === null ? null : Number((amount * multiplier).toFixed(2)));

    const properties = {
      dog_weight: activeOffer.getAttribute('data-dogsize'),
      product_type: activeOffer.getAttribute('data-product-type'),
      product_title: this.dataset.productTitle,
      variant_id: activeOffer.getAttribute('data-variant-id'),
      selling_plan_id: activeOffer.getAttribute('data-selling-plan'),
      quantity: quantity,
      delivery_date: this.dataset.nextChargeDate || QUICK_ADD_DELIVERY_NEXT_ORDER,
      price_original: toMoney(original),
      price_discounted: toMoney(discounted),
      extra_one_time_discount: this.extraDiscountLabel(),
    };

    // Only absent values are dropped — a legitimate 0 (say, a fully discounted
    // line) must still reach Klaviyo rather than vanishing for being falsy.
    Object.keys(properties).forEach((key) => {
      if (properties[key] === null || properties[key] === undefined || properties[key] === '') {
        delete properties[key];
      }
    });

    return properties;
  }

  /**
   * Best-effort only: the subscription is already updated by the time this runs, so
   * a missing or failing Klaviyo embed must never disturb the confirmation state or
   * the redirect. `identify` first because the customer arrived from an email and the
   * event has to land on that profile, not an anonymous one.
   *
   * Not awaited — the redirect is already held for QUICK_ADD_REDIRECT_DELAY_MS, which
   * is the window Klaviyo gets to send the beacon.
   */
  trackAddedToSubscription(trackingEvent) {
    if (!trackingEvent) return;

    const klaviyo = window.klaviyo;
    if (!klaviyo || typeof klaviyo.push !== 'function') return;

    const eventName = this.dataset.klaviyoEvent || QUICK_ADD_KLAVIYO_EVENT;

    try {
      klaviyo.push(['identify', { $email: trackingEvent.email }]);
      klaviyo.push(['track', eventName, trackingEvent.properties]);
    } catch (error) {
      // Analytics is not worth failing a completed add over.
    }
  }

  /**
   * The destination the header account icon points at, resolved at redirect time
   * so global.js has already had its chance to deep-link it.
   */
  resolvePortalUrl() {
    const accountLink = document.querySelector(RECHARGE_PORTAL_LINK_SELECTOR);

    return accountLink?.href || RECHARGE_PORTAL_FALLBACK_URL;
  }

  onSuccess(trackingEvent) {
    this.clearError();
    this.button.removeAttribute('aria-busy');
    this.setLabel(this.dataset.successText);
    // Button stays disabled and isSubmitting stays true so the redirect cannot be double-fired.

    this.trackAddedToSubscription(trackingEvent);

    const portalUrl = this.resolvePortalUrl();

    // Deliberate delay: the redirect replaces the page immediately, so without a short hold the
    // confirmation state would never be perceivable to the customer.
    this.redirectTimer = setTimeout(() => {
      window.location.assign(portalUrl);
    }, QUICK_ADD_REDIRECT_DELAY_MS);
  }
}

if (!customElements.get('pdp-add-to-subscription')) {
  customElements.define('pdp-add-to-subscription', PdpAddToSubscription);
}
